import DetectManager from '../util/detectManager'
import MotionDetector from '../util/motionDetector'
import { NightVisionFilterGL, MotionGLRenderer } from './graph_pipeline_gl'
import { FaceSSD } from './ssd'
import { PBE } from './recognition'
import * as tf from '@tensorflow/tfjs'
import cv from './opencv.js'
import { getEnv } from '../util/setEnv'

const show_per_job_log = false // show really detailed bug large amount of logs

const external_events = {
    set_low_light_detect: 'set_low_light_detect',
    set_low_light_filter: 'set_low_light_filter',
    set_intermed_zoom_scale: 'set_intermed_zoom_scale',
    set_final_zoom_scale: 'set_final_zoom_scale',
    set_intermed_zoom_coord: 'set_intermed_zoom_coord',
    set_final_zoom_coord: 'set_final_zoom_coord',
    set_person_det: 'set_person_det',
    set_motion_det: 'set_motion_det',
    reset_motion_area: 'reset_motion_area',
    pasuse_motion: 'pasuse_motion',
    set_motion_area: 'set_motion_area',
    auto_set_low_light: 'auto_set_low_light',
    auto_set_person_det: 'auto_set_person_det',
    set_low_light_threshold: 'set_low_light_threshold',
    set_motion_threshold: 'set_motion_threshold',
    set_person_det_threshold: 'set_person_det_threshold',
    select_next_jobs: 'select_next_jobs',
    set_face_inference_interval: 'set_face_inference_interval',
    set_face_rec_uncertainty: 'set_face_rec_uncertainty',
    set_face_det: 'set_face_det',
    set_face_det_threshold: 'set_face_det_threshold',
    switch_detect_target: 'switch_detect_target',
    appearence_boxes: 'appearence_boxes',
    appear_timestamp: 'appear_timestamp',
    set_motion_value: 'set_motion_value',
    set_global_inference_interval: 'set_global_inference_interval',
}

const data_tensors = {
    viewport_tensor: 'viewport_tensor',
    zoomarea_tensor: 'zoomarea_tensor',
    nv_int32_tensor: 'nv_int32_tensor',
}

function count_num_claim(src_job, tensor_name) {
    /*
        Counting the number of job had claimed the usage of the tensor.
        We only go through the jobs after the job that creaing the tensor.
        Job that created/init this tensor and jobs before it won't count.
    */
    function check_next(job) {
        if (job.next !== undefined) if (job.next.length > 0) return true
        return false
    }

    let count = 0
    // console.log('src job name', src_job.name)
    if (check_next(src_job)) {
        if (!(src_job instanceof Cond)) {
            for (const job of src_job.next) {
                let claimed = job.claim_tensors()
                if (claimed.includes(tensor_name)) count += 1
                count += count_num_claim(job, tensor_name)
            }
        } else {
            let branch_counts = []
            for (const job of src_job.next) {
                let branch_count = 0
                let claimed = job.claim_tensors()
                for (const tname of claimed) {
                    if (tname == tensor_name) branch_count += 1
                }
                branch_count += count_num_claim(job, tensor_name)
                branch_counts.push(branch_count)
            }
            if (branch_counts[0] != branch_counts[1]) {
                throw Error(
                    `${src_job.name} have exec branch with unevent usage of tensor-${tensor_name}! ${branch_counts}`
                )
            }
            count += branch_counts[0]
        }
    }
    return count
}

class Job {
    constructor(name, next, priority, pipeline) {
        this.pipeline = pipeline
        if (next !== undefined || next !== null)
            this.next = next.sort(
                (job_a, job_b) => job_b.priority - job_a.priority
            )
        else this.next = undefined

        if (priority > 100 || priority < 0)
            throw Error(
                'Priority value exceed maximum litmit of 100/ minimum of 0!'
            )
        this.priority = priority
        this.last_run_time = performance.now()
        this.name = name
        this._cache_external_event = {}
        this._runtime_history = []
        this._run_timestamp = []
        this._runtime_history_limit = 60

        // eslint-disable-next-line
        const { alfredTest2wsxsw2 } = getEnv(location)
        if (!alfredTest2wsxsw2) {
            console = new Proxy(console, {
                get: function (target, prop, receiver) {
                    // disable log except error log
                    if (prop !== 'error') {
                        return function () {}
                    }
                    return Reflect.get(target, prop, receiver)
                },
            })
        }
    }

    cool_down() {
        /* return true if job has already idle for required amount of time */
        return true
    }

    force_run(data) {
        return 'num_warmup' in data || 'force_run' in data
    }

    claim_tensors() {
        /* 
            tell the name of tensors this job will use from the 
            "data" pass by previous job in "_run" method
        */
        return []
    }

    log(...args) {
        if (show_per_job_log) {
            console.debug(
                `[${this.pipeline.constructor.name}-${this.pipeline._build_time_str}][${this.constructor.name}] `,
                ...args
            )
        }
    }

    internal_state() {
        return undefined
    }

    runtime_statisic() {
        if (this._runtime_history.length > 0) {
            let total = 0
            for (const t of this._runtime_history) {
                total += t
            }
            let mean = total / this._runtime_history.length
            let varian = 0
            for (const t of this._runtime_history) {
                varian += Math.pow(t - mean, 2)
            }
            varian /= this._runtime_history.length

            const first_run = Math.min(...this._run_timestamp)
            const last_run = Math.max(...this._run_timestamp)
            const fps =
                this._run_timestamp.length / ((last_run - first_run) / 1000)
            return {
                mean: mean,
                variance: varian,
                count: this._runtime_history.length,
                fps: fps,
            }
        } else {
            return {
                mean: -1,
                variance: -1,
                count: 0,
                fps: 0,
            }
        }
    }

    _record_runtime(td) {
        this._runtime_history.push(td)
        if (this._runtime_history.length > this._runtime_history_limit)
            this._runtime_history.shift()

        this._run_timestamp.push(performance.now())
        if (this._run_timestamp.length > this._runtime_history_limit)
            this._run_timestamp.shift()
        // this.log(`[${this.name}] runtime: ${td}`)
    }

    async run(data) {
        /* 
            Handle warmup counting and tensor ref counting 
            before and after "_run" method
        */
        let elapsed_in = 0
        if (data.frame_timestamp) {
            elapsed_in = new Date().getTime() - data.frame_timestamp.timestamp
        }

        if (data.num_warmup !== undefined) {
            if (typeof data.num_warmup == 'number') data.num_warmup += 1
            else if (data.num_warmup instanceof Array)
                data.num_warmup.push(this.name)
            else throw Error(typeof data.num_warmup)
        }

        const t1 = performance.now()
        const cool = this.cool_down()
        let result = await this._run(data, cool)
        const td = performance.now() - t1
        if (cool || this.force_run(data)) {
            this._record_runtime(td)
            this.log(`[${this.name}] runtime: ${td}, ${cool}, ${data.frame_id}`)
        }

        if (result === undefined) {
            for (const key in data) {
                if (external_events[key] !== undefined) {
                    this._cache_external_event[key] = data[key]
                }
            }
        } else {
            if (Object.keys(this._cache_external_event).length > 0) {
                for (const key in this._cache_external_event) {
                    if (!(key in data))
                        data[key] = this._cache_external_event[key]
                }
            }
            this._cache_external_event = {}
            result._source_job = this.name
        }

        for (const key in result) {
            let item = result[key]
            if (item instanceof tf.Tensor) {
                if (item._ref_count === undefined) {
                    item._ref_count = count_num_claim(this, key)
                } else {
                    // if (this.claim_tensors().includes(key)) item._ref_count -= 1
                    let claimed = this.claim_tensors()
                    for (const tname of claimed) {
                        if (tname == key) item._ref_count -= 1
                    }
                }
                this.log('tensor gc', key, item._ref_count)
            }
        }

        if (data.frame_timestamp) {
            data.frame_timestamp.trace_jobs.push({
                job: this.name,
                elapsed_in,
                elapsed_out:
                    new Date().getTime() - data.frame_timestamp.timestamp,
            })
        }
        return result
    }

    async _run(data, cool_down) {
        /* Actualy processing the input datas */
        throw Error('Not impelmented yet!')
    }
}

export class JobSeq extends Job {
    constructor(name, jobs, next) {
        super(name, jobs, next)
        this.name = name
        this.jobs = jobs.map((v, i, a) => {
            if (i < a.length - 1) v.next = [a[i + 1]]
            return v
        })
        this.priority = Math.max(...jobs.map((j) => j.priority))
        if (next !== undefined)
            this.next = next.sort(
                (job_a, job_b) => job_b.priority - job_a.priority
            )
    }

    claim_tensors() {
        let tensor_names = []
        for (const job of this.jobs) {
            tensor_names = tensor_names.concat(job.claim_tensors())
        }
        return tensor_names
    }

    async _run(data, cool_down) {
        for (const job of this.jobs) {
            data = await job.run(data)
            if (data === undefined || data === null) break
        }
        return data
    }
}

export class Zoom extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.canvas = new OffscreenCanvas(256, 256)
        this.ctx = this.canvas.getContext('2d')
        this.x_center = undefined
        this.y_center = undefined
        this.scale = 1.0
    }

    async create_data_obj(data, zoom_area, frame_or_canvas) {
        let zoomarea_bitmap = await createImageBitmap(
            frame_or_canvas,
            zoom_area[0],
            zoom_area[2],
            zoom_area[1] - zoom_area[0],
            zoom_area[3] - zoom_area[2]
        )

        this.canvas.height = zoomarea_bitmap.height
        this.canvas.width = zoomarea_bitmap.width
        this.ctx.drawImage(zoomarea_bitmap, 0, 0)
        const zoomarea_tensor = tf.browser.fromPixels(this.canvas)
        return {
            zoomarea_bitmap: zoomarea_bitmap,
            zoomarea_tensor: zoomarea_tensor,
            zoom_area: zoom_area,
            ...data,
        }
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .frame
                    ImageBitmap directly extract from video with original resolution
            Will use when "data" have:
                .set_intermed_zoom_coord
                    new coordinate of the cneter of the zoom area for transition animation,
                    this will set "pasuse_motion" to skip every job after Motion.
                .set_final_zoom_coord
                    final zoom coordinate for transition animation, and we will reset new Motion
                    zoom area resolution with "reset_motion_area"
                .set_intermed_zoom_scale
                .set_final_zoom_scale
            Append to "data" in the end:
                .zoomarea_bitmap
                    croped ImageBitmap of zoom area
                .zoomarea_tensor
                    tfjs tensor version of zoomarea_bitmap
                .zoom_area
                    [xmin, xmax, ymin, ymax] of zoom area
            May append to "data" depend on what "data" has:
                .pasuse_motion
                .reset_motion_area
        */
        this.log('tfjs memory: ', tf.memory())
        const frame = data.frame || data.videoCanvas
        const set_coord =
            data.set_intermed_zoom_coord || data.set_final_zoom_coord
        const update_x = set_coord ? set_coord[0] : undefined
        const update_y = set_coord ? set_coord[1] : undefined
        const x_center =
            update_x || this.x_center || Math.floor(frame.width / 2)
        const y_center =
            update_y || this.y_center || Math.floor(frame.height / 2)
        const scale =
            data.set_final_zoom_scale ||
            data.set_intermed_zoom_scale ||
            this.scale ||
            1.0

        if (data.set_intermed_zoom_scale) {
            data[external_events.pasuse_motion] = true
            this.log('ZoomScale-intermed', data.set_intermed_zoom_scale)
        }
        if (data.set_intermed_zoom_coord) {
            data[external_events.pasuse_motion] = true
            this.log('ZoomCoord-intermed', data.set_intermed_zoom_coord)
        }
        if (data.set_final_zoom_scale) {
            data[external_events.reset_motion_area] = true
            this.log('ZoomScale-final', data.set_final_zoom_scale)
        }
        if (data.set_final_zoom_coord) {
            data[external_events.reset_motion_area] = true
            this.log('ZoomCoord-final', data.set_final_zoom_coord)
        }

        this.y_center = y_center
        this.x_center = x_center
        this.scale = scale

        const w = frame.width
        const h = frame.height

        let scaled_w = Number.parseInt(frame.width / scale)
        let scaled_h = Number.parseInt(frame.height / scale)
        // this.log(
        //     `scaled_w: ${scaled_w}, scaled_h: ${scaled_h}, `,
        //     `frame.width: ${frame.width}, frame.height: ${frame.height}, `,
        //     `x_center: ${x_center}, y_center: ${y_center}`)

        let zoom_area = [
            Number.parseInt(x_center - Math.floor(scaled_w / 2)),
            Number.parseInt(x_center + Math.floor(scaled_w / 2)),
            Number.parseInt(y_center - Math.floor(scaled_h / 2)),
            Number.parseInt(y_center + Math.floor(scaled_h / 2)),
        ]

        // bound the zoom area inside the screen
        if (zoom_area[0] < 0) {
            zoom_area[0] = 0
            zoom_area[1] = scaled_w
            this.x_center = Math.floor(scaled_w / 2)
        } else if (zoom_area[1] > w) {
            zoom_area[0] = w - scaled_w
            zoom_area[1] = w
            this.x_center = Math.floor((zoom_area[0] + zoom_area[1]) / 2)
        }
        if (zoom_area[2] < 0) {
            zoom_area[2] = 0
            zoom_area[3] = scaled_h
            this.y_center = Math.floor(scaled_h / 2)
        } else if (zoom_area[3] > h) {
            zoom_area[2] = h - scaled_h
            zoom_area[3] = h
            this.y_center = Math.floor((zoom_area[2] + zoom_area[3]) / 2)
        }

        // let zoomarea_bitmap = await createImageBitmap(
        //     frame,
        //     zoom_area[0], zoom_area[2],
        //     zoom_area[1] - zoom_area[0],
        //     zoom_area[3] - zoom_area[2],
        // )

        // this.canvas.height = zoomarea_bitmap.height
        // this.canvas.width = zoomarea_bitmap.width
        // this.ctx.drawImage(zoomarea_bitmap, 0, 0)
        // const zoomarea_tensor = tf.browser.fromPixels(this.canvas)
        // return {
        //     zoomarea_bitmap: zoomarea_bitmap,
        //     zoomarea_tensor: zoomarea_tensor,
        //     zoom_area: zoom_area,
        //     ...data
        // }
        return await this.create_data_obj(data, zoom_area, frame)
    }
}

export class Motion extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.detector = new MotionDetector()
        this.canvas = new OffscreenCanvas(128, 128)
        this.ctx = this.canvas.getContext('2d')
        this.motion_mtx_size = 8
        this.motion_threshold = 110
        this.last_area = undefined
        this._enable = true
        this._from_last_reset = 0
        this._cool_down_time = 150
    }

    cool_down() {
        const cool =
            performance.now() - this.last_run_time > this._cool_down_time
        if (cool) this.last_run_time = performance.now()
        return cool
    }

    claim_tensors() {
        return [data_tensors.zoomarea_tensor]
    }

    async mtx_to_bbox(motion_mtx) {
        motion_mtx = motion_mtx.squeeze([2])
        let tf_x_vector = tf.mean(motion_mtx, 0)
        let tf_y_vector = tf.mean(motion_mtx, 1)

        let [x_vector, y_vector] = await Promise.all([
            tf_x_vector.array(),
            tf_y_vector.array(),
        ])
        tf_x_vector.dispose()
        tf_y_vector.dispose()
        motion_mtx.dispose()

        let threshold = 5

        let xmin = x_vector.length
        for (let i = 0; i < x_vector.length; i++) {
            if (x_vector[i] > threshold) {
                xmin = i
                break
            }
        }

        let xmax = -1

        for (let i = x_vector.length; i >= 0; i--) {
            if (x_vector[i] > threshold) {
                xmax = i
                break
            }
        }

        let ymin = y_vector.length
        for (let i = 0; i < y_vector.length; i++) {
            if (y_vector[i] > threshold) {
                ymin = i
                break
            }
        }

        let ymax = -1
        for (let i = y_vector.length; i >= 0; i--) {
            if (y_vector[i] > threshold) {
                ymax = i
                break
            }
        }
        // console.debug('motion area: ', [xmin, xmax, ymin, ymax])
        if (
            xmin >= x_vector.length ||
            xmax < 0 ||
            ymin >= y_vector.length ||
            ymax < 0
        )
            return [0, x_vector.length, 0, y_vector.length]
        else return [xmin, xmax, ymin, ymax]
    }

    async get_motion(frame, data) {
        const frame_h =
            frame instanceof tf.Tensor ? frame.shape[0] : frame.height
        const frame_w =
            frame instanceof tf.Tensor ? frame.shape[1] : frame.width

        let detected = undefined

        const ta = performance.now()
        if (frame instanceof ImageBitmap) {
            this.ctx.drawImage(
                frame,
                0,
                0,
                frame.width,
                frame.height,
                0,
                0,
                this.canvas.width,
                this.canvas.width
            )
            detected = this.detector.detect_motion(this.canvas)
        } else if (frame instanceof tf.Tensor) {
            let resized_frame = tf.image.resizeNearestNeighbor(frame, [
                this.canvas.height,
                this.canvas.width,
            ])
            detected = this.detector.detect_motion(resized_frame)
            resized_frame.dispose()
        } else {
            throw Error(`Motion getting unsupport frame type: ${frame}`)
        }

        detected = await detected
        const td = performance.now() - ta
        this.log('motion time: ', td)
        if (data.set_motion_value) {
            detected = data.set_motion_value
        }
        this.log(
            `motion value: ${detected}, threshold: ${this.motion_threshold}`
        )

        if (detected > this.motion_threshold) {
            const grid_coord = await this.mtx_to_bbox(
                this.detector.currMotionMap
            )
            const area_coord = [
                Number.parseInt(
                    frame_w * (grid_coord[0] / this.motion_mtx_size)
                ),
                Number.parseInt(
                    frame_w *
                        (Math.min(grid_coord[1] + 1, this.motion_mtx_size) /
                            this.motion_mtx_size)
                ),
                Number.parseInt(
                    frame_h * (grid_coord[2] / this.motion_mtx_size)
                ),
                Number.parseInt(
                    frame_h *
                        (Math.min(grid_coord[3] + 1, this.motion_mtx_size) /
                            this.motion_mtx_size)
                ),
            ]
            const area_coord_fp = {
                x_min: area_coord[0] / frame_w,
                x_max: area_coord[1] / frame_w,
                y_min: area_coord[2] / frame_h,
                y_max: area_coord[3] / frame_h,
                cls_score: Number.parseFloat(detected),
                cls: 'motion_area',
            }
            this.last_area = area_coord
            return {
                motion_area: area_coord,
                motion_area_box: area_coord_fp,
                ...data,
            }
        } else {
            // HACK: return motion_area_box to let graphicHandler know nothing is detected
            // and ignore motion_area to skip viewport OP.
            return {
                // motion_area: [0, 1, 0, 1],
                motion_area_box: {},
                ...data,
            }
        }
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or .zoomarea_bitmap or .frame
                    Use image of zoomarea to compute motion if possible, use entire frame otherwise
            Will use when "data" have:
                .reset_motion_area
                    call motion detector 's external_reset method to reset the image size
                    it use to comput motion
                .pasuse_motion
                    skip this job and every job after
            Append to "data" in the end:
                .motion_area
                    Current or last area where motion is detected
            May append to "data" depend on what "data" has:
                .motion_area_box
                    if new motion is detected, we will send normalized boudning box of motion
                    area of [xmin, xmax, ymin, ymax] back. box is normalized due to the fact
                    it maybe use to draw box annotation on preview canvas, and preview canvas
                    may have different size as video frame.
        */
        const frame = data.zoomarea_tensor || data.zoomarea_bitmap || data.frame

        if (data.set_global_inference_interval) {
            this._cool_down_time = data.set_global_inference_interval
        }

        if (data.set_motion_det !== undefined) {
            this._enable = data.set_motion_det
        }

        if (data.set_motion_threshold !== undefined) {
            if (data.set_motion_threshold <= 0)
                throw Error(
                    `Invalid motion threshold value: ${data.set_motion_threshold}`
                )
            this.log('set_threhsold: ', data.set_motion_threshold)
            this.motion_threshold = data.set_motion_threshold
        }

        if (data.reset_motion_area !== undefined) {
            this.last_area = undefined
            // HACK: detector not support imagebitmap to tensor

            if (frame instanceof ImageBitmap) {
                this.ctx.drawImage(
                    frame,
                    0,
                    0,
                    frame.width,
                    frame.height,
                    0,
                    0,
                    this.canvas.width,
                    this.canvas.width
                )
                this.detector.external_reset(this.canvas)
            } else {
                this.detector.external_reset(frame)
            }
            this._from_last_reset = 0
        } else {
            this._from_last_reset++
        }

        if (frame instanceof tf.Tensor) {
            this.last_area = !this.last_area
                ? [0, frame.shape[1], 0, frame.shape[0]]
                : this.last_area
        } else {
            this.last_area = !this.last_area
                ? [0, frame.width, 0, frame.height]
                : this.last_area
        }

        if (
            data.set_motion_area !== undefined &&
            this.detector.frameCount > this.detector.dropFrame &&
            this._from_last_reset > 10
        ) {
            let new_motion_boxes = data.set_motion_area
            for (const box of new_motion_boxes) {
                this.detector.add_motion(
                    [
                        Number.parseInt(
                            box[0] * this.detector.longDiff.shape[1]
                        ),
                        Number.parseInt(
                            box[1] * this.detector.longDiff.shape[1]
                        ),
                        Number.parseInt(
                            box[2] * this.detector.longDiff.shape[0]
                        ),
                        Number.parseInt(
                            box[3] * this.detector.longDiff.shape[0]
                        ),
                    ],
                    128
                )
                try {
                } catch (error) {
                    //do nothing
                    window.logManager.error('Fail to add motion', error)
                }
            }
            this.log(
                'set_motion_area - _from_last_reset ',
                this._from_last_reset
            )
        } else {
            this.log('cool down after reset', this._from_last_reset)
        }

        if (!this.force_run(data)) {
            if (data.pasuse_motion !== undefined || !this._enable) {
                // job is not enable or been pause by another job in pipeline
                if (frame instanceof tf.Tensor) frame.dispose()
                return undefined
            } else if (!cool_down) {
                return {
                    motion_area: this.last_area,
                    ...data,
                }
            } else {
                return this.get_motion(frame, data)
            }
        } else {
            return this.get_motion(frame, data)
        }
    }
}

export class ViewPort extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.canvas = new OffscreenCanvas(1280, 720)
        this.ctx = this.canvas.getContext('2d')
    }

    cool_down() {
        // const cool = performance.now() - this.last_run_time > 250
        // if (cool) this.last_run_time = performance.now()
        return true
    }

    claim_tensors() {
        return [data_tensors.zoomarea_tensor]
    }

    async get_viewport_tensor(frame, x, y, w, h) {
        let viewport_tensor = undefined
        if (frame instanceof tf.Tensor) {
            viewport_tensor = frame.slice([y, x], [h, w])
        } else {
            const viewport_bitmap = await createImageBitmap(frame, x, y, w, h)
            this.canvas.height = h
            this.canvas.width = w
            this.ctx.drawImage(viewport_bitmap, 0, 0)
            viewport_tensor = tf.browser.fromPixels(this.canvas)
        }
        return viewport_tensor
    }

    adjsut_vp_area(box, frame) {
        /*
        Add 20% padding to viewport size and make viewport square
        */
        const frame_height =
            frame instanceof tf.Tensor ? frame.shape[0] : frame.height
        const frame_width =
            frame instanceof tf.Tensor ? frame.shape[1] : frame.width
        let xmin = box[0]
        let xmax = box[1]
        let ymin = box[2]
        let ymax = box[3]
        let h = box[3] - box[2]
        let w = box[1] - box[0]

        h = Math.min(Math.max(h * 1.3, frame_height * 0.25), frame_height)
        w = Math.min(Math.max(w * 1.3, frame_width * 0.25), frame_width)
        let size = Math.min(Math.max(h, w), Math.min(frame_width, frame_height))

        if (xmin <= 0 && xmax >= frame_width - 1) {
            xmin = Math.max((xmin + xmax) / 2 - size / 2, 0)
            xmax = Math.min(xmin + size, frame_width)
        } else if (xmin <= 0) {
            xmin = 0
            xmax = size
        } else if (xmax >= frame_width - 1) {
            xmin = frame_width - size
            xmax = frame_width
        } else {
            xmin = Math.max((xmin + xmax) / 2 - size / 2, 0)
            xmax = Math.min(xmin + size, frame_width)
        }

        if (ymin <= 0 && ymax >= frame_height - 1) {
            ymin = Math.max((ymin + ymax) / 2 - size / 2, 0)
            ymax = Math.min(ymin + size, frame_height)
        } else if (ymin <= 0) {
            ymin = 0
            ymax = size
        } else if (ymax >= frame_height - 1) {
            ymin = frame_height - size
            ymax = frame_height
        } else {
            ymin = Math.max((ymin + ymax) / 2 - size / 2, 0)
            ymax = Math.min(ymin + size, frame_height)
        }

        const new_box = [
            Number.parseInt(xmin),
            Number.parseInt(xmax),
            Number.parseInt(ymin),
            Number.parseInt(ymax),
        ]
        return new_box
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or .zoomarea_bitmap or .frame
                    source to crop viewport from, zoomarea has higher priority than full frame.
                .motion_area
                    the area viewport will be crop.
                    "motion_area" should be formated to [xmin, xmax, ymin, ymax]
            Append to "data" in the end:
                .viewport_tensor
                    Tensor of viewport crop
        */
        const frame = data.zoomarea_tensor || data.zoomarea_bitmap || data.frame
        const frame_h =
            frame instanceof tf.Tensor ? frame.shape[0] : frame.height
        const frame_w =
            frame instanceof tf.Tensor ? frame.shape[1] : frame.width

        if (this.force_run(data)) {
            data.motion_area = [0, frame_w, 0, frame_h]
        }

        if (data.motion_area === undefined) {
            if (frame instanceof tf.Tensor) frame.dispose()
            return undefined
        }
        const box = this.adjsut_vp_area(data.motion_area, frame)

        if (cool_down || this.force_run(data)) {
            if (box !== undefined) {
                const xmin = box[0]
                const ymin = box[2]
                const h = box[3] - box[2]
                const w = box[1] - box[0]
                this.log('Viewport box: ', [xmin, ymin, w, h])
                const viewport_tensor = await this.get_viewport_tensor(
                    frame,
                    xmin,
                    ymin,
                    w,
                    h
                )

                let viewport_box_fp = {
                    x_min: box[0] / frame_w,
                    x_max: box[1] / frame_w,
                    y_min: box[2] / frame_h,
                    y_max: box[3] / frame_h,
                    cls_score: 1.0,
                    cls: 'viewport_area',
                }

                return {
                    viewport_tensor: viewport_tensor,
                    viewport_box: viewport_box_fp,
                    viewport_area: box,
                    ...data,
                }
            } else {
                const viewport_tensor = await this.get_viewport_tensor(
                    frame,
                    0,
                    0,
                    frame.width,
                    frame.height
                )
                // viewport_tensor.dispose()
                let viewport_box_fp = {
                    x_min: 0.0,
                    x_max: 1.0,
                    y_min: 0.0,
                    y_max: 1.0,
                    cls_score: 1.0,
                    cls: 'viewport_area',
                }
                return {
                    viewport_tensor: viewport_tensor,
                    viewport_box: viewport_box_fp,
                    viewport_area: [0, frame_w, 0, frame_h],
                    ...data,
                }
            }
        } else {
            if (frame instanceof tf.Tensor) frame.dispose()
            return data
        }
    }
}

export class SSD extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this._enable = false
        this._onoff = true // this switch will be use to temporarily turn on/off SSD by low light det.
        this._default_cool_down = 333
        this._overdrive_cool_down = 2000
        this._overdrive_meter = 0
        this._prev_detect = []
        this._output_threshold = 0.4
    }

    claim_tensors() {
        return [data_tensors.viewport_tensor]
    }

    cool_down() {
        const Td = performance.now() - this.last_run_time
        let cool = true
        // 3fps * 30 sec = 90
        if (this._overdrive_meter > 30) {
            cool = Td > this._overdrive_cool_down
            this.pipeline.send_input_event({
                reset_motion_area: true,
            })
            this.log(
                'reset_motion_area with static detect: ',
                this._overdrive_meter
            )
            this._overdrive_meter = 0
        } else {
            cool = Td > this._default_cool_down
        }

        if (cool) {
            // if (this._overdrive_meter > 30) {
            //     this._overdrive_meter = 0
            // }
            this.last_run_time = performance.now()
        }
        return cool
    }

    async build_detector() {
        this.detector = new DetectManager()
    }

    map_box_to_native(boxes, pred_size, viewport_area, native_size) {
        const vp_x = viewport_area[0]
        const vp_y = viewport_area[2]
        const vp_w = viewport_area[1] - viewport_area[0]
        const vp_h = viewport_area[3] - viewport_area[2]
        for (const box of boxes) {
            box.x_min =
                Number.parseInt((box.x_min / pred_size[0]) * vp_w) + vp_x
            box.x_max =
                Number.parseInt((box.x_max / pred_size[0]) * vp_w) + vp_x
            box.y_min =
                Number.parseInt((box.y_min / pred_size[1]) * vp_h) + vp_y
            box.y_max =
                Number.parseInt((box.y_max / pred_size[1]) * vp_h) + vp_y

            box.x_min /= native_size.w
            box.x_max /= native_size.w
            box.y_min /= native_size.h
            box.y_max /= native_size.h
        }
        return boxes
    }

    handle_detect_feedback_loop(boxes, zh, zw) {
        function get_box_area(box) {
            let w = box.x_max - box.x_min
            let h = box.y_max - box.y_min
            return w * h
        }

        function IOU(box_a, box_b) {
            let area_a = get_box_area(box_a)
            let area_b = get_box_area(box_b)
            let intersect = {
                x_max: Math.min(box_a.x_max, box_b.x_max),
                x_min: Math.max(box_a.x_min, box_b.x_min),
                y_max: Math.min(box_a.y_max, box_b.y_max),
                y_min: Math.max(box_a.y_min, box_b.y_min),
            }
            intersect = get_box_area(intersect)
            let iou = intersect / ((area_a + area_b) / 2)
            return iou
        }

        // record the number of time we use detection to trigger motion
        if (boxes.length > 0) {
            if (this._prev_detect.length > 0) {
                let same_obj_position = false
                for (const pbox of this._prev_detect) {
                    for (const cbox of boxes) {
                        // this.log('Past IOU: ', IOU(pbox, cbox), pbox, cbox)
                        if (IOU(pbox, cbox) > 0.7) {
                            this._overdrive_meter += 1
                            same_obj_position = true
                            break
                        }
                    }
                    if (same_obj_position) break
                }
                if (!same_obj_position) this._overdrive_meter = 0
            }
            this.log(
                '_overdrive_meter:',
                this._overdrive_meter,
                boxes,
                this._prev_detect
            )
            this._prev_detect = boxes

            let vboxes = []
            for (const box of boxes) {
                vboxes.push([box.x_min, box.x_max, box.y_min, box.y_max])
            }
            this.log('feedback boxes: ', vboxes)
            this.pipeline.send_input_event({
                set_motion_area: vboxes,
            })
        }
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .viewport_tensor
                    we only use image crop by viewport to inferece, and viewport tensor wiil
                    be resize to 256x256 before feeding into SSD
                .motion_area
                    bounding box of area viewport_tensor is croped, it is use to mapping 
                    coordinate of box of SSD prediction back to video frame's coordinate system.
            Will use when "data" have:
                .set_person_det
                    disable or enable this job
            Append to "data" in the end:
                .detect_boxes
                    normazlied bounding box from detection result. 
        */
        if (this.detector === undefined) await this.build_detector()

        if (data[external_events.set_global_inference_interval]) {
            this._default_cool_down = data.set_global_inference_interval
        }

        if (data.set_person_det !== undefined) {
            this._enable = data.set_person_det
            this._onoff = data.set_person_det
            this.log('set_person_detection: ', data.set_person_det)
        }
        if (data.auto_set_person_det !== undefined) {
            if (this._enable) {
                this._onoff = data.auto_set_person_det
                this.log(
                    'auto set person detection: ',
                    data.auto_set_person_det
                )
            }
        }
        if (data.set_person_det_threshold !== undefined) {
            if (
                data.set_person_det_threshold < 0 ||
                data.set_person_det_threshold > 1
            )
                throw Error(
                    `Invalid person detection threshold: ${data.set_person_det_threshold}`
                )
            this._output_threshold = data.set_person_det_threshold
            this.log(
                'set_person_det_threshold: ',
                data.set_person_det_threshold
            )
        }

        const frame = data.viewport_tensor
        if (frame !== undefined) {
            const should_run = this._onoff && this._enable && cool_down
            if (should_run || this.force_run(data)) {
                const resized = tf.image.resizeBilinear(frame, [256, 256])
                const T1 = performance.now()
                let boxes = await this.detector.predict({
                    snapshot: resized,
                    w: resized.shape[1],
                    h: resized.shape[0],
                })
                const Td = performance.now() - T1
                this.log('inference time: ', Td)

                const zw = data.zoom_area[1] - data.zoom_area[0]
                const zh = data.zoom_area[3] - data.zoom_area[2]

                // this.log('debug viewport area: ', data.viewport_area)
                boxes = this.map_box_to_native(
                    boxes,
                    [256, 256],
                    data.viewport_area,
                    { w: zw, h: zh }
                )
                this.handle_detect_feedback_loop(
                    JSON.parse(JSON.stringify(boxes)),
                    zh,
                    zw
                )
                resized.dispose()
                boxes = boxes.filter(
                    (box) => box.cls_score > this._output_threshold
                )
                return {
                    detect_boxes: boxes,
                    ...data,
                }
            } else {
                // this.log('not detect', this._onoff, this._enable)
                return data
            }
        } else {
            // this.log('no frame')
            return data
        }
    }
}

export class LowLightDetect extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.canvas = new OffscreenCanvas(256, 256)
        this.ctx = this.canvas.getContext('2d')
        this.too_bright = false
        this._enable = false
        this.dark_threshold = 0.26
        this.birght_threshold = 0.34
    }

    claim_tensors() {
        return [data_tensors.zoomarea_tensor]
    }

    cool_down() {
        const cool = performance.now() - this.last_run_time > 30000
        if (cool) this.last_run_time = performance.now()
        return cool
    }

    brightness(pixels) {
        const brightness = tf.tidy(() => {
            const preprocessedInput = tf.div(
                pixels.asType('float32'),
                tf.scalar(255)
            )
            const [r, g, b] = tf.split(preprocessedInput, 3, 2)
            let Y = tf.mul(r, tf.scalar(0.299))
            Y = tf.add(Y, tf.mul(g, tf.scalar(0.587)))
            Y = tf.add(Y, tf.mul(b, tf.scalar(0.114)))
            // Y = tf.image.resizeBilinear(Y, [256, 256])
            Y = tf.avgPool(Y, 2, 2, 'same')
            Y = tf.avgPool(Y, 2, 2, 'same')
            Y = tf.avgPool(Y, 2, 2, 'same')
            return Y.mean().dataSync()
        })
        return brightness
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or.zoomarea_bitmap or.frame
                    source to crop viewport from, zoomarea has higher priority than full frame.
            Will use when "data" have:
                .set_low_light_detect
                    disable or enable this job
            Append to "data" in the end:
                .set_person_det
                    disable person detection(SSD) if it's too dark
                .set_low_light_filter
                    enable low-light-filter if it 's too dark
                .auto_set_low_light
                    tell the outputCallback it should update the copy of pipeline configs on main thread
        */
        let check_before_turn_on = false

        if (data[external_events.set_low_light_detect] !== undefined) {
            this._enable = data[external_events.set_low_light_detect]
            check_before_turn_on = this._enable
        }

        if (data[external_events.set_low_light_threshold] !== undefined) {
            if (!data.set_low_light_threshold.dark_threshold instanceof Number)
                throw Error(
                    `Invalid low light detection threhsold: ${data.set_low_light_threshold.dark_threshold}`
                )
            if (
                !data.set_low_light_threshold.bright_threshold instanceof Number
            )
                throw Error(
                    `Invalid low light detection threhsold: ${data.set_low_light_threshold.bright_threshold}`
                )
            this.dark_threshold = data.set_low_light_threshold.dark_threshold
            this.bright_threshold =
                data.set_low_light_threshold.bright_threshold
        }

        const should_run = cool_down && this._enable
        if (should_run || check_before_turn_on || this.force_run(data)) {
            const frame =
                data.zoom_canvas ||
                data.zoomarea_tensor ||
                data.zoomarea_bitmap ||
                data.frame
            let brightness = 2.0

            if (frame instanceof ImageBitmap) {
                this.canvas.height = frame.height
                this.canvas.width = frame.width
                this.ctx.drawImage(frame, 0, 0)

                const frame_tensor = tf.browser.fromPixels(this.canvas)
                brightness = this.brightness(frame_tensor)
            } else if (
                frame.constructor.name == 'HTMLCanvasElement' ||
                frame.constructor.name == 'OffscreenCanvas'
            ) {
                const frame_tensor = tf.browser.fromPixels(frame)
                brightness = this.brightness(frame_tensor)
            } else if (frame instanceof tf.Tensor) {
                brightness = tf.tidy(() => {
                    const square = tf.image.resizeNearestNeighbor(frame, [
                        256,
                        256,
                    ])
                    return this.brightness(square)
                })
            } else {
                throw Error(`LowLightDetect get unsupport frame type: ${frame}`)
            }

            this.log('brightness: ', brightness)
            // NOTE: Won't actually apply low light setting when force run by warmup process
            if (!this.force_run(data)) {
                if (this.too_bright && brightness < this.dark_threshold) {
                    this.too_bright = false
                    this.pipeline.send_input_event({
                        auto_set_low_light: true,
                        auto_set_person_det: false,
                        set_low_light_filter: !this.too_bright,
                    })
                } else if (
                    !this.too_bright &&
                    brightness > this.birght_threshold
                ) {
                    this.too_bright = true
                    this.pipeline.send_input_event({
                        auto_set_low_light: false,
                        auto_set_person_det: true,
                        set_low_light_filter: !this.too_bright,
                    })
                } else {
                    // INIT state
                    this.too_bright =
                        brightness >
                        (this.birght_threshold + this.dark_threshold) / 2
                    if (this.too_bright) {
                        this.pipeline.send_input_event({
                            auto_set_low_light: false,
                            auto_set_person_det: true,
                            set_low_light_filter: !this.too_bright,
                        })
                    } else {
                        this.pipeline.send_input_event({
                            auto_set_low_light: true,
                            auto_set_person_det: false,
                            set_low_light_filter: !this.too_bright,
                        })
                    }
                }
            }
            // data[external_events.set_low_light_filter] = !this.too_bright
        }

        return data
    }
}

export class LowLightFilter extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.apply_filter = true
        this.canvas = new OffscreenCanvas(640, 480)
        this.ctx = this.canvas.getContext('2d')
    }

    claim_tensors() {
        return [data_tensors.zoomarea_tensor]
    }

    applyNightVision(image_tensor) {
        return tf.tidy(() => {
            const [r, g, b] = tf.split(image_tensor, 3, 2)
            let Y = tf.mul(r, tf.scalar(0.299))
            Y = tf.add(Y, tf.mul(g, tf.scalar(0.587)))
            Y = tf.add(Y, tf.mul(b, tf.scalar(0.114)))
            const U = tf.mul(Y, tf.scalar(1.2))
            const V = Y
            const YUV = tf.concat([Y, U, V], 2).clipByValue(0, 255)
            return tf.cast(YUV, 'int32')
        })
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or.frame
                    image source to apply filter, zoomarea has higher priority than full frame.
            Will use when "data" have:
                .set_low_light_filter
                    disable or enable this job
            Append to "data" in the end:
                .nv_int32_tensor
                    tensor of image after apply filter
        */
        let frame_tensor = undefined
        if (data[external_events.set_low_light_filter] !== undefined)
            this.apply_filter = data[external_events.set_low_light_filter]

        if (this.apply_filter) {
            if (data.zoomarea_tensor) {
                frame_tensor = data.zoomarea_tensor
            } else {
                this.canvas.height = data.frame.height
                this.canvas.width = data.frame.width
                this.ctx.drawImage(data.frame, 0, 0)
                frame_tensor = tf.browser.fromPixels(this.canvas)
            }

            let nv_int32_tensor = this.applyNightVision(frame_tensor)
            return {
                nv_int32_tensor: nv_int32_tensor,
                ...data,
            }
        } else {
            return data
        }
    }
}

export class DrawVideoCanvas extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
    }

    claim_tensors() {
        return [data_tensors.nv_int32_tensor]
    }

    async toPixels(tensor, canvas = null) {
        const [height, width] = tensor.shape.slice(0, 2)
        // convert to rgba by adding alpha channel
        const alpha = tf.fill([height, width, 1], 255, 'int32')
        const rgba = tf.concat([tensor, alpha], 2)
        tf.dispose([alpha, tensor])

        const bytes = await rgba.data()
        const pixelData = new Uint8ClampedArray(bytes)
        if (canvas !== null) {
            const imageData = new ImageData(pixelData, width, height)
            const ctx = canvas.getContext('2d')
            ctx.putImageData(imageData, 0, 0)
        }
        tf.dispose(rgba)
        return bytes
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .nv_int32_tensor or.zoomarea_bitmap or.frame
                    image source to converted to ImageBitmap and send back as result or 
                    draw on the videoCanvas depend on pipeline's mode(main thread or worker mode).
                    nv_int32_tensor has higher priority than zoomarea_bitmap/full frame.
                .videoCanvas
                .videoCanvasCtx
            Append to "data" in the end:
                .videoFrameUpdated
                    ImageBitmap of final image that had been draw on videoCanvas 
                    or main thread handler
        */
        const frame =
            data.nv_int32_tensor ||
            data.zoomarea_bitmap ||
            data.frame ||
            data.zoom_canvas
        const ctx = data.videoCanvasCtx
        const canvas = data.videoCanvas
        // canvas.height = data.frame.height
        // canvas.width = data.frame.width

        if (frame instanceof ImageBitmap || frame instanceof OffscreenCanvas) {
            ctx.drawImage(
                frame,
                0,
                0,
                frame.width,
                frame.height,
                0,
                0,
                canvas.width,
                canvas.height
            )
            return {
                videoFrameUpdated: frame,
                ...data,
            }
        } else {
            let resized = tf.image.resizeBilinear(frame, [
                canvas.height,
                canvas.width,
            ])
            await this.toPixels(resized, canvas)
            // await tf.browser.toPixels(tf.cast(resized, "float32").div(tf.scalar(255)), canvas)
            resized.dispose()
            const frame_bitmap = await createImageBitmap(canvas)
            return {
                videoFrameUpdated: frame_bitmap,
                ...data,
            }
        }
    }
}

export class Cleanup extends Job {
    constructor(name, next, priority, pipeline, ret_data) {
        /*
            WARN: if this job been put after DrawCanvas or SSD
            and ret_data is set to true, it will trigger double output of the same frame
            or detection result by resending "data" to pipeline's output callback from "run".
        */
        super(name, next, priority, pipeline)
        this._ret_data = ret_data
    }

    async _run(data, cool_down) {
        let rmkey = []
        for (const key in data) {
            let item = data[key]
            if (item instanceof tf.Tensor) {
                if (item._ref_count <= 0) {
                    item.dispose()
                    rmkey.push(key)
                }
            }
        }
        for (const key of rmkey) {
            delete data[key]
            this.log('remove tensor', key, ` by ${this.name}`)
        }
        if (this._ret_data) {
            this.log('return clean data: ', data)
            return data
        }
    }
}

export class DrawNvCanvas extends Job {
    /*
        Using GL shader to apply filter directly on canvas, which can reduce 
        CPU -> GPU -> CPU data transder overhead.
    */
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.gl_renderer = undefined
        this._enable_nv = false

        this.gl_canvas = new OffscreenCanvas(640, 480)
        this.cache_canvas = new OffscreenCanvas(640, 480)
        this.cache_canvas_ctx = this.cache_canvas.getContext('2d')
        this.gl_renderer = new NightVisionFilterGL(this.gl_canvas)
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or.frame
                    image source to apply filter, zoomarea has higher priority than full frame.
            Will use when "data" have:
                .set_low_light_filter
                    disable or enable this job
            Append to "data" in the end:
                .videoFrameUpdated
                    ImageBitmap of final image that had been draw on videoCanvas 
                    or main thread handler
        */

        if (data[external_events.set_low_light_filter] !== undefined)
            this._enable_nv = data[external_events.set_low_light_filter]

        const frame = data.zoomarea_bitmap || data.frame

        const ctx = data.videoCanvasCtx
        const canvas = data.videoCanvas
        // canvas.height = data.frame.height
        // canvas.width = data.frame.width
        this.gl_canvas.height = data.frame.height
        this.gl_canvas.width = data.frame.width

        if (!this._enable_nv) {
            ctx.drawImage(
                frame,
                0,
                0,
                frame.width,
                frame.height,
                0,
                0,
                canvas.width,
                canvas.height
            )
            return {
                videoFrameUpdated: frame,
                ...data,
            }
        } else {
            this.gl_renderer.render(frame, data.frame.width, data.frame.height)
            const frame_bitmap = await createImageBitmap(this.gl_canvas)
            return {
                videoFrameUpdated: frame_bitmap,
                ...data,
            }
        }
    }
}

export class MotionGL extends Job {
    /*
        Using GL shader to apply filter directly on canvas, which can reduce 
        CPU -> GPU -> CPU data transder overhead.
    */
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this.gl_renderer = undefined
        this.last_run_time = performance.now()
        this.motion_threshold = 140

        this._enable = true
        this._cool_down_time = 200
        this._from_last_reset = 0

        this.frame_counter = 0
        this.ref_frame = -1
        this.setup(160, 120)
    }

    setup(width, height) {
        this.ref_canvas = new OffscreenCanvas(width, height)
        this.ref_ctx = this.ref_canvas.getContext('2d')
        this.ref_bitmap = null

        this.gl_canvas = new OffscreenCanvas(width, height)
        // this.gl_renderer = new BoundedFlowGL(this.gl_canvas)
        this.gl_renderer = new MotionGLRenderer(this.gl_canvas)
    }

    reset_canvas_size(canvas, height, width) {
        if (canvas.height != height || canvas.width != width)
            this.setup(width, height)
    }

    mtx_to_bbox(x_vector, y_vector, normal = true) {
        let threshold = this.motion_threshold * 0.625

        let xmin = x_vector.length
        for (let i = 0; i < x_vector.length; i++) {
            if (x_vector[i] > threshold) {
                xmin = i
                break
            }
        }

        let xmax = -1

        for (let i = x_vector.length - 1; i >= 0; i--) {
            if (x_vector[i] > threshold) {
                xmax = i
                break
            }
        }

        let ymin = y_vector.length
        for (let i = y_vector.length - 1; i >= 0; i--) {
            if (y_vector[i] > threshold) {
                ymin = i
                break
            }
        }
        ymin = y_vector.length - ymin

        let ymax = -1
        for (let i = 0; i < y_vector.length; i++) {
            if (y_vector[i] > threshold) {
                ymax = i
                break
            }
        }
        ymax = y_vector.length - ymax

        if (
            xmin >= x_vector.length &&
            xmax < 0 &&
            ymin >= y_vector.length &&
            ymax < 0
        ) {
            return undefined
        } else {
            return normal
                ? [
                      xmin / x_vector.length,
                      xmax / x_vector.length,
                      ymin / y_vector.length,
                      ymax / y_vector.length,
                  ]
                : [xmin, xmax, ymin, ymax]
        }
    }

    cool_down() {
        const cool =
            performance.now() - this.last_run_time > this._cool_down_time
        if (cool) this.last_run_time = performance.now()
        return cool
    }

    async get_motion(frame, data) {
        const reduce_motion = this.gl_renderer.render(
            this.ref_canvas,
            frame,
            frame.width,
            frame.height
        )
        // DEBUG: motion_bitmap is used for outputing motion map from gl canvas
        // const motion_bitmap = await createImageBitmap(
        //     this.gl_canvas,
        //     0,
        //     0,
        //     this.gl_canvas.width / 1,
        //     this.gl_canvas.height / 1
        // )

        let motion_area = this.mtx_to_bbox(
            reduce_motion.horizontal_motion,
            reduce_motion.vertical_motion
        )
        const h_mean = reduce_motion.horizontal_motion.reduce((a, b) =>
            Math.max(a, b)
        )
        const v_mean = reduce_motion.vertical_motion.reduce((a, b) =>
            Math.max(a, b)
        )
        const mean_motion = (h_mean + v_mean) / 2

        this.log(
            '[MotionGL] motion_value: ',
            mean_motion,
            ', area: ',
            motion_area
        )
        this.last_motion_value = mean_motion

        this.frame_counter++
        if (this.frame_counter - this.ref_frame > 0) {
            this.ref_frame = this.frame_counter
            this.ref_ctx.drawImage(frame, 0, 0)
            this.log('[MotionGL] Draw reference canvas')
        }

        if (motion_area !== undefined && mean_motion > this.motion_threshold) {
            const area_coord_fp = {
                x_min: motion_area[0],
                x_max: motion_area[1],
                y_min: motion_area[2],
                y_max: motion_area[3],
                cls_score: 1.0,
                cls: 'motion_area',
            }
            motion_area = [
                Number.parseInt(motion_area[0] * frame.width),
                Number.parseInt(motion_area[1] * frame.width),
                Number.parseInt(motion_area[2] * frame.height),
                Number.parseInt(motion_area[3] * frame.height),
            ]
            this.last_area = motion_area
            this.log('[MotionGL] enable')

            return {
                // videoFrameUpdated: motion_bitmap,
                motion_area: motion_area,
                motion_area_box: area_coord_fp,
                ...data,
            }
        } else {
            return {
                // videoFrameUpdated: motion_bitmap,
                motion_area_box: {},
                ...data,
            }
        }
    }

    handle_motion_event(frame, data) {
        if (data.reset_motion_area !== undefined) {
            this.last_area = undefined
            this.gl_renderer.clear_motion_texture()
            this._from_last_reset = 0
        } else {
            this._from_last_reset++
        }

        if (
            data.set_motion_area !== undefined &&
            this.gl_renderer.frameCount > this.gl_renderer.dropFrame &&
            this._from_last_reset > 10
        ) {
            let new_motion_boxes = data.set_motion_area
            for (const box of new_motion_boxes) {
                try {
                    this.gl_renderer.add_motion(box[0], box[1], box[2], box[3])
                } catch (error) {
                    //do nothing
                    window.logManager.error('Fail to add motion', error)
                }
            }
            this.log(
                'set_motion_area - _from_last_reset ',
                this._from_last_reset
            )
        } else {
            this.log('cool down after reset', this._from_last_reset)
        }
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or.frame
                    image source to apply filter, zoomarea has higher priority than full frame.
            Will use when "data" have:
                .set_low_light_filter
                    disable or enable this job
            Append to "data" in the end:
                .videoFrameUpdated
                    ImageBitmap of final image that had been draw on videoCanvas 
                    or main thread handler
        */

        if (data[external_events.set_global_inference_interval]) {
            this._cool_down_time = data.set_global_inference_interval
        }

        if (data.set_motion_det !== undefined) {
            this._enable = data.set_motion_det
        }

        if (data.set_motion_threshold !== undefined) {
            if (data.set_motion_threshold <= 0)
                throw Error(
                    `Invalid motion threshold value: ${data.set_motion_threshold}`
                )
            this.log('set_threhsold: ', data.set_motion_threshold)
            this.motion_threshold = data.set_motion_threshold
        }

        const frame = data.zoomarea_bitmap || data.frame
        // data.set_motion_area = [[0.1, 0.9, 0.2, 0.3]]

        this.reset_canvas_size(this.gl_canvas, frame.height, frame.width)
        this.reset_canvas_size(this.ref_canvas, frame.height, frame.width)
        this.handle_motion_event(frame, data)
        // this.reset_canvas_size(canvas, data.frame.height, data.frame.width)

        if (this.ref_frame === -1) {
            this.ref_ctx.drawImage(frame, 0, 0)
            this.ref_ctx.fillRect(
                0,
                0,
                this.ref_canvas.width,
                this.ref_canvas.height
            )
            this.ref_frame = this.frame_counter
        }

        if (!this.force_run(data)) {
            if (data.pasuse_motion !== undefined || !this._enable) {
                // job is not enable or been pause by another job in pipeline
                if (frame instanceof tf.Tensor) frame.dispose()
                return undefined
            } else if (!cool_down) {
                if (this.last_motion_value > this.motion_threshold) {
                    return {
                        motion_area: this.last_area,
                        ...data,
                    }
                } else {
                    return data
                }
            } else {
                return await this.get_motion(frame, data)
            }
        } else {
            return await this.get_motion(frame, data)
        }
    }
}

export class SlimZoom extends Zoom {
    async create_data_obj(data, zoom_area, frame_or_canvas) {
        if (this.canvas.height != data.videoCanvas.height)
            this.canvas.height = data.videoCanvas.height
        if (this.canvas.width != data.videoCanvas.width)
            this.canvas.width = data.videoCanvas.width
        this.ctx.drawImage(
            data.frame || data.videoCanvas,
            zoom_area[0],
            zoom_area[2],
            zoom_area[1] - zoom_area[0],
            zoom_area[3] - zoom_area[2],
            0,
            0,
            this.canvas.width,
            this.canvas.height
        )
        // this.log(
        //     `canvas w: ${this.canvas.width}, canvas h: ${this.canvas.height}, `,
        //     `videoCanvas w: ${data.videoCanvas.width}, videoCanvas h: ${data.videoCanvas.height}, `)
        return {
            // videoFrameUpdated: data.frame,
            zoom_canvas: this.canvas,
            zoom_area: zoom_area,
            ...data,
        }
    }
}

export class SlimDrawNvCanvas extends DrawNvCanvas {
    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or.frame
                    image source to apply filter, zoomarea has higher priority than full frame.
            Will use when "data" have:
                .set_low_light_filter
                    disable or enable this job
            Append to "data" in the end:
                .videoFrameUpdated
                    ImageBitmap of final image that had been draw on videoCanvas 
                    or main thread handler
        */

        if (data[external_events.set_low_light_filter] !== undefined)
            this._enable_nv = data[external_events.set_low_light_filter]

        if (data.zoom_canvas === undefined)
            throw Error('Missing zoom canvas from SilmZoom!')

        const frame = data.zoom_canvas

        if (this.gl_canvas.height != frame.height)
            this.gl_canvas.height = frame.height
        if (this.gl_canvas.width != frame.width)
            this.gl_canvas.width = frame.width

        if (!this._enable_nv) {
            return {
                videoCanvasUpdated: frame,
                ...data,
            }
        } else {
            this.gl_renderer.render(
                frame,
                frame.width,
                frame.height,
                this.gl_canvas.width,
                this.gl_canvas.height
            )
            // ctx.drawImage(this.gl_canvas, 0, 0)
            return {
                videoCanvasUpdated: this.gl_canvas,
                ...data,
            }
        }
    }
}

export class SlimMotionGL extends MotionGL {
    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or.frame
                    image source to apply filter, zoomarea has higher priority than full frame.
            Will use when "data" have:
                .set_low_light_filter
                    disable or enable this job
            Append to "data" in the end:
                .videoFrameUpdated
                    ImageBitmap of final image that had been draw on videoCanvas 
                    or main thread handler
        */

        if (data.set_motion_det !== undefined) {
            this._enable = data.set_motion_det
        }

        const frame = data.videoCanvas
        const ctx = data.videoCanvasCtx
        const canvas = data.videoCanvas

        // this.reset_canvas_size(this.gl_canvas, frame.height, frame.width)
        this.reset_canvas_size(
            this.gl_canvas,
            data.frame.height,
            data.frame.width
        )
        this.reset_canvas_size(
            this.ref_canvas,
            data.frame.height,
            data.frame.width
        )

        if (this.ref_frame === -1) {
            this.ref_ctx.drawImage(frame, 0, 0)
            this.ref_ctx.fillRect(0, 0, 1000, 1000)
            this.ref_frame = this.frame_counter
        }

        if (!this._enable || !cool_down) {
            return {
                videoCanvasUpdated: frame,
                ...data,
            }
        } else {
            this.log('[MotionGL] enable')
            this.gl_renderer.render(
                this.ref_canvas,
                frame,
                frame.width,
                frame.height
            )

            this.frame_counter++
            if (this.frame_counter - this.ref_frame > 5) {
                this.ref_frame = this.frame_counter
                this.ref_ctx.drawImage(frame, 0, 0)
                this.log('[MotionGL] Draw reference canvas')
            }
            ctx.drawImage(
                this.gl_canvas,
                0,
                0,
                frame.width / 4,
                frame.height / 4,
                0,
                0,
                frame.width / 4,
                frame.height / 4
            )

            return {
                videoCanvasUpdated: canvas,
                ...data,
            }
        }
    }
}

export class CreateImageBitmap extends Job {
    constructor(name, next, priority, pipeline, cool_down_time = 250) {
        super(name, next, priority, pipeline)
        this.last_run_time = performance.now()
        this._cache_external_event = {}
        this._cool_down_time = cool_down_time
    }

    cool_down() {
        const cool =
            performance.now() - this.last_run_time > this._cool_down_time
        if (cool) this.last_run_time = performance.now()
        return cool
    }

    async _run(data, cool_down) {
        const frame = data.zoom_canvas || data.videoCanvas
        if (data[external_events.set_global_inference_interval]) {
            this._cool_down_time = data.set_global_inference_interval
        }

        if (cool_down || this.force_run(data)) {
            data.frame = await createImageBitmap(frame)
            return data
        } else {
            return undefined
        }
    }
}

export class Cond extends Job {
    constructor(name, next_tr, next_fn, priority, pipeline, switch_func) {
        super(name, [next_tr, next_fn], priority, pipeline)
        this.last_run_time = performance.now()
        this.next_tr = next_tr
        this.next_fn = next_fn
        this._switch_func = switch_func

        this._cond_cache_event = {}
        this._cond_cache_for = 'next_tr'
        this._warmup_ptr = false
    }

    cool_down() {
        return true
    }

    claim_tensors() {
        // special case Handle by count_num_claim, shouldn't be call directly
        return []
    }

    async _run(data, cool_down) {
        let output = {}

        if (this.force_run(data)) {
            const following_jobs = [this.next_tr, this.next_fn].reduce(
                function (accumulator, currentValue) {
                    return accumulator.concat(currentValue)
                },
                []
            )
            let next = null
            if (this._warmup_ptr) {
                next = this.next_tr
                this._warmup_ptr = false
            } else {
                next = this.next_fn
                this._warmup_ptr = true
            }

            return {
                ...data,
                select_next_jobs: next,
            }
        }

        if (this._switch_func(data)) {
            output = {
                ...data,
                select_next_jobs: this.next_tr,
            }
            if (this._cond_cache_for == 'next_tr') {
                this.log('true func load cache', this._cond_cache_event)
                output = {
                    ...output,
                    ...this._cond_cache_event,
                }
                this._cond_cache_event = {}
            }
            this._cond_cache_for = 'next_fn'
        } else {
            output = {
                ...data,
                select_next_jobs: this.next_fn,
            }
            if (this._cond_cache_for == 'next_fn') {
                this.log('false func load cache', this._cond_cache_event)
                output = {
                    ...output,
                    ...this._cond_cache_event,
                }
                this._cond_cache_event = {}
            }
            this._cond_cache_for = 'next_tr'
        }
        for (const key in data) {
            if (external_events[key] !== undefined) {
                this._cond_cache_event[key] = data[key]
            }
        }
        return output
    }
}

export class Connector extends Job {
    constructor(
        name,
        next,
        priority,
        pipeline,
        data_modifier,
        claim_tensor_func
    ) {
        super(name, next, priority, pipeline)
        this.data_modifier = data_modifier
        this._claim_tensors = claim_tensor_func
    }

    cool_down() {
        return true
    }

    claim_tensors() {
        return this._claim_tensors()
    }

    async _run(data, cool_down) {
        if (this.data_modifier !== undefined) data = this.data_modifier(data)
        return data
    }
}

export class FaceKPSSD extends SSD {
    /*
        SSD detector that only detect human faces with size of 24~128px.
        It also output 5 point face keypoint along with the detected faces.
        {left/right eyes, nose, left/right end of the mouth}
    */
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
        this._output_threshold = 0.5
        this._loading_promise = null
    }

    async build_detector() {
        this.detector = new FaceSSD(
            '/webapp/camera/face_kp_web_model_fp16/model.json'
        )
        this._loading_promise = this.detector.load_weights()
        await this._loading_promise
    }

    map_box_to_native(boxes, pred_size, viewport_area, native_size) {
        const vp_x = viewport_area[0]
        const vp_y = viewport_area[2]
        const vp_w = viewport_area[1] - viewport_area[0]
        const vp_h = viewport_area[3] - viewport_area[2]
        for (const box of boxes) {
            box.x_min =
                Number.parseInt((box.x_min / pred_size[0]) * vp_w) + vp_x
            box.x_max =
                Number.parseInt((box.x_max / pred_size[0]) * vp_w) + vp_x
            box.y_min =
                Number.parseInt((box.y_min / pred_size[1]) * vp_h) + vp_y
            box.y_max =
                Number.parseInt((box.y_max / pred_size[1]) * vp_h) + vp_y

            box.x_min /= native_size.w
            box.x_max /= native_size.w
            box.y_min /= native_size.h
            box.y_max /= native_size.h
            box.keypoints = box.keypoints.map((v, i) => {
                if (i % 2 == 0)
                    return ((v / pred_size[1]) * vp_h + vp_y) / native_size.h
                else return ((v / pred_size[0]) * vp_w + vp_x) / native_size.w
            })
        }
        return boxes
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .viewport_tensor
                    we only use image crop by viewport to inferece, and viewport tensor wiil
                    be resize to 256x256 before feeding into SSD
                .motion_area
                    bounding box of area viewport_tensor is croped, it is use to mapping 
                    coordinate of box of SSD prediction back to video frame's coordinate system.
            Will use when "data" have:
                .set_face_det
                    disable or enable this job
            Append to "data" in the end:
                .detect_boxes
                    normazlied bounding box from detection result. 
        */
        if (this.detector == undefined) {
            // model not yet created
            await this.build_detector()
        } else if (this._loading_promise !== null) {
            // model is loading
            await this._loading_promise
        }

        if (data.set_face_det !== undefined) {
            this._enable = data.set_face_det
            this._onoff = data.set_face_det
            this.log('set_face_detection: ', data.set_face_det)
        }
        if (data.auto_set_face_det !== undefined) {
            if (this._enable) {
                this._onoff = data.auto_set_face_det
                this.log('auto set person detection: ', data.auto_set_face_det)
            }
        }
        if (data.set_face_det_threshold !== undefined) {
            if (
                data.set_face_det_threshold < 0 ||
                data.set_face_det_threshold > 1
            )
                throw Error(
                    `Invalid person detection threshold: ${data.set_face_det_threshold}`
                )
            this._output_threshold = data.set_face_det_threshold
            this.log('set_face_det_threshold: ', data.set_face_det_threshold)
        }
        if (
            data.set_face_inference_interval !== undefined ||
            data.set_global_inference_interval !== undefined
        ) {
            const inter_ms =
                data.set_face_inference_interval ||
                data.set_global_inference_interval
            if (50 <= inter_ms && inter_ms <= 1000) {
                this._default_cool_down = inter_ms
            } else {
                throw Error(
                    `Invalid set_face_inference_interval: ${inter_ms}, it should be in the range of 100~1000 ms`
                )
            }
        }

        let frame = data.viewport_tensor
        if (this.force_run(data)) {
            // NOTE: using dummy input to insure the execution
            frame = tf.zeros([512, 384, 3], 'float32')
        }

        if (frame !== undefined) {
            const should_run = this._onoff && this._enable && cool_down
            if (should_run || this.force_run(data)) {
                const resized = this.detector.preprocess(frame)
                // let boxes = await this.detector.multi_scale_predict(resized)
                const T1 = performance.now()
                let boxes = await this.detector.predict(resized)
                const Td = performance.now() - T1
                this.log('inference time: ', Td)

                const zw = data.zoom_area[1] - data.zoom_area[0]
                const zh = data.zoom_area[3] - data.zoom_area[2]

                // this.log('debug viewport area: ', data.viewport_area)
                boxes = this.map_box_to_native(
                    boxes,
                    [512, 384],
                    data.viewport_area,
                    { w: zw, h: zh }
                )
                this.handle_detect_feedback_loop(
                    JSON.parse(JSON.stringify(boxes)),
                    zh,
                    zw
                )
                resized.dispose()
                boxes = boxes.filter(
                    (box) => box.cls_score > this._output_threshold
                )

                return {
                    detect_boxes: boxes,
                    ...data,
                }
            } else {
                // this.log('not detect', this._onoff, this._enable, cool_down, data.frame_id)
                return data
            }
        } else {
            // this.log('no frame: ', data.frame_id)
            return data
        }
    }
}

export class FixSizeViewPort extends ViewPort {
    constructor(name, next, priority, pipeline, viewport_box_fp) {
        super(name, next, priority, pipeline)
        this.canvas = new OffscreenCanvas(1280, 720)
        this.ctx = this.canvas.getContext('2d')
        this.viewport_box_fp = viewport_box_fp
    }

    async _run(data, cool_down) {
        /*
            Expect "data" to have:
                .zoomarea_tensor or .zoomarea_bitmap or .frame
                    source to crop viewport from, zoomarea has higher priority than full frame.
                .motion_area
                    the area viewport will be crop.
                    "motion_area" should be formated to [xmin, xmax, ymin, ymax]
            Append to "data" in the end:
                .viewport_tensor
                    Tensor of viewport crop
        */
        const frame = data.zoomarea_tensor || data.zoomarea_bitmap || data.frame
        const frame_h =
            frame instanceof tf.Tensor ? frame.shape[0] : frame.height
        const frame_w =
            frame instanceof tf.Tensor ? frame.shape[1] : frame.width

        if (this.force_run(data)) {
            data.motion_area = [0, frame_w, 0, frame_h]
        }

        if (data.motion_area === undefined) {
            if (frame instanceof tf.Tensor) frame.dispose()
            return undefined
        }

        if (cool_down || this.force_run(data)) {
            const viewport_tensor = await this.get_viewport_tensor(
                frame,
                Number.parseInt(this.viewport_box_fp.x_min * frame_w),
                Number.parseInt(this.viewport_box_fp.y_min * frame_h),
                Number.parseInt(
                    (this.viewport_box_fp.x_max - this.viewport_box_fp.x_min) *
                        frame_w
                ),
                Number.parseInt(
                    (this.viewport_box_fp.y_max - this.viewport_box_fp.y_min) *
                        frame_h
                )
            )
            // viewport_tensor.dispose()
            let viewport_box_fp = {
                x_min: this.viewport_box_fp.x_min,
                x_max: this.viewport_box_fp.x_max,
                y_min: this.viewport_box_fp.y_min,
                y_max: this.viewport_box_fp.y_max,
                cls_score: 1.0,
                cls: 'viewport_area',
            }
            return {
                viewport_tensor: viewport_tensor,
                viewport_box: viewport_box_fp,
                viewport_area: [0, frame_w, 0, frame_h],
                ...data,
            }
        } else {
            if (frame instanceof tf.Tensor) frame.dispose()
            return data
        }
    }
}

export class CropFace extends Job {
    cool_down() {
        return true
    }

    claim_tensors() {
        return []
    }

    standarlize_box(box, img_h, img_w) {
        /*
        box : {
            x_min: int ∈ [0, image width],
            y_min: int ∈ [0, image height],
            ...
        }
        */
        const h = box.y_max - box.y_min
        const w = box.x_max - box.x_min
        const center = {
            x: (box.x_max + box.x_min) / 2,
            y: (box.y_max + box.y_min) / 2,
        }
        const pad_ratio = 0.5

        let box_size = Math.max(h, w) * (1 + pad_ratio)
        let new_box = {
            ...box,
            x_min: Math.max(0, center.x - box_size / 2),
            y_min: Math.max(0, center.y - box_size / 2),
            x_max: Math.min(img_w, center.x + box_size / 2),
            y_max: Math.min(img_h, center.y + box_size / 2),
        }
        return new_box
    }

    async _run(data, cool_down) {
        const frame = data.zoomarea_bitmap || data.frame || data.zoomarea_tensor
        let boxes = data.detect_boxes
        const w = frame.width
        const h = frame.height

        if (this.force_run(data))
            boxes = [
                {
                    x_min: 0,
                    y_min: 0,
                    x_max: w - 1,
                    y_max: h - 1,
                    keypoints: Array(10).fill(0.0),
                    cls: 'face',
                },
            ]

        if (boxes) {
            // let face_boxes = boxes.filter((v) => v.cls == 'face')
            const crops = []
            let update_boxes = []

            for (let i = 0; i < Math.min(boxes.length, 16); i++) {
                let box = boxes[i]

                if (box.cls == 'face') {
                    if (frame instanceof ImageBitmap) {
                        let _box = {
                            ...box,
                            x_min: Number.parseInt(box.x_min * w),
                            y_min: Number.parseInt(box.y_min * h),
                            x_max: Number.parseInt(box.x_max * w),
                            y_max: Number.parseInt(box.y_max * h),
                        }
                        _box = this.standarlize_box(_box, h, w)

                        let crop = await createImageBitmap(
                            frame,
                            _box.x_min,
                            _box.y_min,
                            _box.x_max - _box.x_min,
                            _box.y_max - _box.y_min
                        )

                        const update_box = {
                            ...box,
                            x_min: _box.x_min / w,
                            x_max: _box.x_max / w,
                            y_min: _box.y_min / h,
                            y_max: _box.y_max / h,
                        }
                        update_boxes.push(update_box)

                        crop.box = update_box
                        crops.push(crop)
                        this.log('face crop: ', box, _box)
                    } else if (frame instanceof tf.Tensor) {
                        throw Error('Not implemented yet!')
                    } else {
                        throw Error('!!!???')
                    }
                } else {
                    update_boxes.push(box)
                }
            }

            if (frame instanceof tf.Tensor) {
                // stack crops into single tensor !
                throw Error('NotImplemnted!')
            }
            return {
                ...data,
                face_crops: crops,
                detect_boxes: update_boxes,
            }
        } else {
            return data
        }
    }
}

export class AlignFace extends Job {
    constructor(name, next, priority, pipeline, face_patch_size) {
        super(name, next, priority, pipeline)
        this.face_patch_size = face_patch_size
        this.canvas_shift = new OffscreenCanvas(
            face_patch_size,
            face_patch_size
        )
        this.canvas_rot = new OffscreenCanvas(face_patch_size, face_patch_size)
        // NOTE: opencv don't like offscreen canvas
        // this.canvas_rot = document.createElement('canvas')
        this.canvas_rot.height = face_patch_size
        this.canvas_rot.width = face_patch_size

        this.mean_pose = {
            left_eye: [0.3396711, 0.46394727],
            right_eye: [0.66725796, 0.46436372],
            nose_tip: [0.50275546, 0.65724117],
            left_lip: [0.37932193, 0.7973507],
            right_lip: [0.63820577, 0.79864484],
            mean: [0.505442444, 0.63630954],
        }
    }

    cool_down() {
        return true
    }

    claim_tensors() {
        return []
    }

    not_on_border(box) {
        // return false
        // assert normalized coordinate from bbox
        if (box.x_min < 1e-4 && box.x_max < 1 - 1e-4) {
            // bbox touching the top but doesn't touch bottom edge.
            return false
        } else if (box.x_min > 1e-4 && box.x_max > 1 - 1e-4) {
            return false
        } else if (box.y_min < 1e-4 && box.y_max < 1 - 1e-4) {
            return false
        } else if (box.y_min > 1e-4 && box.y_max > 1 - 1e-4) {
            return false
        } else {
            return true
        }
    }

    kp_array2dict(box_with_kp) {
        let kp = box_with_kp.keypoints
        const box_h = box_with_kp.y_max - box_with_kp.y_min
        const box_w = box_with_kp.x_max - box_with_kp.x_min
        const mapped_dict = {
            left_eye: [
                ((kp[1] - box_with_kp.x_min) / box_w) * this.face_patch_size,
                ((kp[0] - box_with_kp.y_min) / box_h) * this.face_patch_size,
            ],
            right_eye: [
                ((kp[3] - box_with_kp.x_min) / box_w) * this.face_patch_size,
                ((kp[2] - box_with_kp.y_min) / box_h) * this.face_patch_size,
            ],
            nose: [
                ((kp[5] - box_with_kp.x_min) / box_w) * this.face_patch_size,
                ((kp[4] - box_with_kp.y_min) / box_h) * this.face_patch_size,
            ],
            mouth_left: [
                ((kp[7] - box_with_kp.x_min) / box_w) * this.face_patch_size,
                ((kp[6] - box_with_kp.y_min) / box_h) * this.face_patch_size,
            ],
            mouth_right: [
                ((kp[9] - box_with_kp.x_min) / box_w) * this.face_patch_size,
                ((kp[8] - box_with_kp.y_min) / box_h) * this.face_patch_size,
            ],
        }
        return mapped_dict
    }

    async _face_align(src_bitmap, landmarks) {
        /**
         Rotate & shift image of face according to landmark on {dst_canvas}, and draw it back to the {src_bitmap}.
         return false if given face isn't looking at the camera, return true otherwise.

         @param src_bitmap [ImageBitmap]
         @param landmarks [landmakr object: {}]
         @return [bool]
         **/
        this.log('align landmark:', landmarks, src_bitmap)

        let target_mou_dis = Math.pow(
            (this.mean_pose['left_lip'][0] - this.mean_pose['right_lip'][0]) *
                this.canvas_shift.width,
            2
        )
        target_mou_dis += Math.pow(
            (this.mean_pose['left_lip'][1] - this.mean_pose['right_lip'][1]) *
                this.canvas_shift.height,
            2
        )
        target_mou_dis = Math.sqrt(target_mou_dis)

        let target_eye_dis = Math.pow(
            (this.mean_pose['left_eye'][0] - this.mean_pose['right_eye'][0]) *
                this.canvas_shift.width,
            2
        )
        target_eye_dis += Math.pow(
            (this.mean_pose['left_eye'][1] - this.mean_pose['right_eye'][1]) *
                this.canvas_shift.height,
            2
        )
        target_eye_dis = Math.sqrt(target_eye_dis)

        let l_eye = landmarks['left_eye']
        let r_eye = landmarks['right_eye']
        let eye_rotate = Math.atan2(
            Math.abs(l_eye[1] - r_eye[1]),
            Math.abs(l_eye[0] - r_eye[0])
        )
        eye_rotate = l_eye[1] > r_eye[1] ? eye_rotate : -eye_rotate
        let eye_dis = Math.sqrt(
            Math.pow(l_eye[1] - r_eye[1], 2) + Math.pow(l_eye[0] - r_eye[0], 2)
        )
        let eye_scale = target_mou_dis / eye_dis

        let l_mou = landmarks['mouth_left']
        let r_mou = landmarks['mouth_right']
        let mou_rotate = Math.atan2(
            Math.abs(l_mou[1] - r_mou[1]),
            Math.abs(l_mou[0] - r_mou[0])
        )
        mou_rotate = l_mou[1] > r_mou[1] ? mou_rotate : -mou_rotate
        let mou_dis = Math.sqrt(
            Math.pow(l_mou[1] - r_mou[1], 2) + Math.pow(l_mou[0] - r_mou[0], 2)
        )
        let mou_scale = target_mou_dis / mou_dis

        let mean_rotate = (eye_rotate + mou_rotate) / 2
        let mean_scale = (eye_scale + mou_scale) / 2

        function inside(point, vs) {
            // ray-casting algorithm based on
            // http://www.ecse.rpi.edu/Homepages/wrf/Research/Short_Notes/pnpoly.html
            var x = point[0],
                y = point[1]

            var inside = false
            for (var i = 0, j = vs.length - 1; i < vs.length; j = i++) {
                var xi = vs[i][0],
                    yi = vs[i][1]
                var xj = vs[j][0],
                    yj = vs[j][1]

                var intersect =
                    yi > y != yj > y &&
                    x < ((xj - xi) * (y - yi)) / (yj - yi) + xi
                if (intersect) inside = !inside
            }
            return inside
        }

        function point_dis(a, b) {
            return Math.sqrt(Math.pow(a[0] - b[0]) + Math.pow(a[1] - b[1]))
        }

        const poor_angle =
            !inside(landmarks['nose'], [l_eye, r_eye, l_mou, r_mou]) ||
            point_dis(landmarks['nose'], l_mou) < 5 ||
            point_dis(landmarks['nose'], r_mou) < 5 ||
            point_dis(landmarks['nose'], l_eye) < 5 ||
            point_dis(landmarks['nose'], r_eye) < 5

        // let mean_point = landmarks['nose']
        let mean_point = [0, 0]
        for (let k in landmarks) {
            mean_point[0] += landmarks[k][0]
            mean_point[1] += landmarks[k][1]
        }
        mean_point[0] /= Object.keys(landmarks).length
        mean_point[1] /= Object.keys(landmarks).length

        let center_shift = [
            this.canvas_shift.width * this.mean_pose.mean[0] - mean_point[0],
            this.canvas_shift.height * this.mean_pose.mean[1] - mean_point[1],
        ]

        // if (poor_angle) mean_rotate /= 2

        let dst_ctx = this.canvas_shift.getContext('2d')
        dst_ctx.clearRect(
            0,
            0,
            this.canvas_shift.width,
            this.canvas_shift.height
        )
        dst_ctx.drawImage(
            src_bitmap,
            0,
            0,
            src_bitmap.width,
            src_bitmap.height,
            center_shift[0],
            center_shift[1],
            this.canvas_shift.width,
            this.canvas_shift.height
        )
        dst_ctx.resetTransform()

        dst_ctx = this.canvas_rot.getContext('2d')
        dst_ctx.clearRect(0, 0, this.canvas_rot.width, this.canvas_rot.height)
        dst_ctx.translate(this.canvas_rot.width / 2, this.canvas_rot.height / 2)
        dst_ctx.rotate(mean_rotate)
        // dst_ctx.scale(mean_scale, mean_scale)
        dst_ctx.drawImage(
            this.canvas_shift,
            -(this.canvas_rot.width / 2),
            -(this.canvas_rot.height / 2),
            this.canvas_rot.width,
            this.canvas_rot.height
        )
        dst_ctx.resetTransform()

        const aligned_patch = await createImageBitmap(
            this.canvas_rot,
            0,
            0,
            this.canvas_rot.width,
            this.canvas_rot.height
        )

        // HACK: temp disable poor angle
        return poor_angle && false ? undefined : aligned_patch
    }

    async _run(data, cool_down) {
        const crops = data.face_crops
        const boxes = data.detect_boxes
        // NOTE: boxes.length >= crops.length, boxes could contain non-face boxes

        if (crops && boxes) {
            const kps = boxes
                .filter((v) => v.cls == 'face')
                .map((v, i) => this.kp_array2dict(v))
            let aligned = []
            for (let i = 0; i < crops.length; i++) {
                if (this.not_on_border(boxes[i])) {
                    const crop = crops[i]
                    const kp = kps[i]
                    let align_or_not = await this._face_align(crop, kp)
                    aligned.push(align_or_not)
                } else {
                    aligned.push(undefined)
                }
            }
            return { ...data, align_face_crops: aligned }
        } else {
            return data
        }
    }
}

export class OpenCVAlignFace extends AlignFace {
    constructor(name, next, priority, pipeline, face_patch_size) {
        super(name, next, priority, pipeline, face_patch_size)

        this.canvas_out = new OffscreenCanvas(face_patch_size, face_patch_size)
        this.canvas_out.height = face_patch_size
        this.canvas_out.width = face_patch_size

        // NOTE: opencv don't like offscreen canvas
        this.src_scale = 2
        this.canvas_rot = new OffscreenCanvas(face_patch_size, face_patch_size)
        this.canvas_rot.height = face_patch_size * this.src_scale
        this.canvas_rot.width = face_patch_size * this.src_scale
    }

    async _face_align(src_bitmap, landmarks) {
        /****************************************************************
         Rotate & shift image of face according to landmark on {dst_canvas}, and draw it back to the {src_bitmap}.
         return false if given face isn't looking at the camera, return true otherwise.

         @param src_bitmap [ImageBitmap]
         @param landmarks [landmakr object: {}]
         @return [bool]
         *****************************************************************/
        this.log('face crop size:', src_bitmap.width, src_bitmap.height)
        // this.canvas_rot.width = Math.min(320, src_bitmap.width)
        // this.canvas_rot.height = Math.min(320, src_bitmap.height)

        const ctx = this.canvas_rot.getContext('2d')
        const src_w = src_bitmap.width
        const src_h = src_bitmap.height
        const src_scale = this.canvas_rot.width / Math.max(src_w, src_h)

        let _src_w = Number.parseInt(src_scale * src_w)
        let _src_h = Number.parseInt(src_scale * src_h)
        const dx = Number.parseInt((this.canvas_rot.width - _src_w) / 2)
        const dy = Number.parseInt((this.canvas_rot.height - _src_h) / 2)

        let src = [
            ...landmarks['left_eye'],
            ...landmarks['right_eye'],
            ...landmarks['nose'],
            ...landmarks['mouth_left'],
            ...landmarks['mouth_right'],
        ]
        src = src.map((v, i) => {
            return i % 2 == 0
                ? v + dx / this.src_scale
                : v + dy / this.src_scale
        })
        src = cv.matFromArray(5, 2, cv.CV_32F, src)

        const norm_dst = [
            ...this.mean_pose['left_eye'],
            ...this.mean_pose['right_eye'],
            ...this.mean_pose['nose_tip'],
            ...this.mean_pose['left_lip'],
            ...this.mean_pose['right_lip'],
        ]
        let dst = cv.matFromArray(
            5,
            2,
            cv.CV_32F,
            norm_dst.map((v) => v * this.face_patch_size)
        )

        const trans_mtx = cv.estimateAffinePartial2D(src, dst)

        ctx.drawImage(src_bitmap, dx, dy, _src_w, _src_h)
        const src_mat = cv.imread(this.canvas_rot)
        const dst_mat = new cv.Mat()
        cv.warpAffine(
            src_mat,
            dst_mat,
            trans_mtx,
            new cv.Size(this.face_patch_size, this.face_patch_size),
            cv.INTER_LINEAR,
            cv.BORDER_CONSTANT,
            new cv.Scalar()
        )
        cv.imshow(this.canvas_rot, dst_mat)
        const ctx_out = this.canvas_out.getContext('2d')
        ctx_out.clearRect(0, 0, this.canvas_out.width, this.canvas_out.height)
        ctx_out.filter = 'blur(7px)'
        ctx_out.drawImage(
            src_bitmap,
            0,
            0,
            this.canvas_out.width,
            this.canvas_out.height
        )

        ctx_out.filter = 'none'
        ctx_out.drawImage(
            this.canvas_rot,
            0,
            0,
            this.canvas_rot.width,
            this.canvas_rot.height,
            0,
            0,
            this.canvas_out.width,
            this.canvas_out.height
        )

        const aligned_patch = await createImageBitmap(
            this.canvas_out,
            0,
            0,
            this.canvas_out.width,
            this.canvas_out.height
        )

        return aligned_patch
    }

    make_box_square(box) {
        const center = {
            x: (box.x_min + box.x_max) / 2,
            y: (box.y_min + box.y_max) / 2,
        }
        const w = box.x_max - box.x_min
        const h = box.y_max - box.y_min
        const long_size = Math.abs(w - h)
        box.x_min = Number.parseInt(box.x_min - (long_size - w) / 2)
        box.x_max = Number.parseInt(box.x_max + (long_size - w) / 2)
        box.y_min = Number.parseInt(box.y_min - (long_size - h) / 2)
        box.y_max = Number.parseInt(box.y_max + (long_size - h) / 2)
        return box
    }

    _run(data) {
        if (this.force_run(data)) {
            /* Just pasting data throuth. Since this job don't using any tfjs component, there is no need to warmup */
            return data
        } else {
            if (cv) {
                return super._run(data)
            } else {
                return undefined
            }
        }
    }
}

export class FaceEmbed extends Job {
    constructor(name, next, priority, pipeline, use_frame = false) {
        super(name, next, priority, pipeline)
        const model_url = '/webapp/camera/pbe_stage2_web_model_fp16/model.json'
        this.model = new PBE(model_url)
        this.uncertainty_threshold = 0.7
        this.canvas = new OffscreenCanvas(...this.model.input_img_size)
        this.ctx = this.canvas.getContext('2d')
        this.use_frame = use_frame // using data.frame at _run as input
        this._loading_promise = this.model.load_weights()
    }

    cool_down() {
        // Run this job base on detector inferece freqency.
        return true
    }

    claim_tensors() {
        return []
    }

    get_faces_embed(faces) {
        return tf.tidy(() => {
            let embed_list = []

            for (const face of faces) {
                if (face !== undefined) {
                    if (
                        face.embedding !== undefined &&
                        face.uncertainty !== undefined
                    ) {
                        embed_list.push({
                            embedding: face.embedding,
                            uncertainty: face.uncertainty,
                        })
                    } else {
                        this.ctx.drawImage(
                            face,
                            0,
                            0,
                            this.canvas.width,
                            this.canvas.height
                        )
                        const face_tensor = this.model.preprocess(this.canvas)
                        let pred = this.model.predict(face_tensor)
                        const exp_uncertainty = tf.exp(pred.uncertainty)
                        const sigma_sq = exp_uncertainty.sum()
                        const sigma_sq_val = sigma_sq.dataSync()

                        if (sigma_sq_val > this.uncertainty_threshold) {
                            embed_list.push(undefined)
                        } else {
                            // NOTE: WebGL tensor won't make it through WebWorker's IPC transfer,
                            //       convert to Array is nessary.
                            embed_list.push({
                                embedding: pred.embedding.dataSync(),
                                uncertainty: exp_uncertainty.dataSync(),
                            })
                        }
                        this.log('pred: ', pred, sigma_sq_val)
                        sigma_sq.dispose()
                        exp_uncertainty.dispose()
                        pred.embedding.dispose()
                        pred.uncertainty.dispose()
                    }
                } else {
                    embed_list.push(undefined)
                }
            }

            return embed_list
        })
    }

    async _run(data, cool_down) {
        let faces = this.use_frame
            ? data.frame
            : data.align_face_crops || data.face_crops
        if (this.force_run(data)) {
            // feed dump image for warmup inference
            faces = [this.canvas]
            this.log('force run data: ', data)
        }

        if (this.model.model === undefined) {
            if (this._loading_promise) {
                await this._loading_promise
            } else {
                return undefined
            }
        }
        this.log('_run ', faces)

        if (data.set_face_rec_uncertainty !== undefined) {
            const new_thres = data.set_face_rec_uncertainty
            this.log('set_face_rec_uncertainty: ', new_thres)
            if (0.1 <= new_thres < 4) {
                this.uncertainty_threshold = new_thres
            } else {
                throw Error(
                    `Invalid face recognition uncertainty threhsold: ${new_thres}, it should in the range of [0.1,10)`
                )
            }
        }

        if (faces) {
            return tf.tidy(() => {
                data.face_embed = this.get_faces_embed(faces)

                if (data.frame !== undefined) {
                    data.upload_face_crop = data.frame
                } else {
                    if (data.align_face_crops) {
                        data.upload_face_crop = data.align_face_crops
                        delete data.align_face_crops
                    } else if (data.face_crops) {
                        data.upload_face_crop = data.face_crops
                        delete data.face_crops
                    }
                }
                this.log('result data: ', data)
                this.log('tf memory: ', tf.memory())
                return data
            })
        } else {
            return data
        }
    }
}

export class DetectWatcher extends Job {
    constructor(name, next, priority, pipeline) {
        super(name, next, priority, pipeline)
    }

    cool_down() {
        return true
    }

    claim_tensors() {
        return []
    }

    async _run(data, cool_down) {
        if (data.detect_boxes !== undefined) {
            // if detector not in cool down time
            if (data.detect_boxes.length == 0) {
                // if detctor dont see anything special
                this.pipeline.send_input_event({
                    switch_detect_target: 'person',
                })
            } else {
                const det_cls = data.detect_boxes[0].cls
                if (det_cls == 'person') {
                    this.pipeline.send_input_event({
                        switch_detect_target: 'face',
                    })
                }
            }
        }
        return data
    }
}

export class AppearenceFilter extends Job {
    constructor(name, next, priority, pipeline, use_frame = false) {
        super(name, next, priority, pipeline)
        this.prev_pos_and_time = {}
        this.lookback_time = 5000
        this.metric_threshold = 2
        this.use_frame = use_frame
        this.max_record_per_person = 1
    }

    cool_down() {
        return true
    }

    claim_tensors() {
        return []
    }

    record(data) {
        const name_to_box = data.appearence_boxes
        // const timestamp = data.appear_timestamp
        const timestamp = new Date().getTime()
        this.log('[appf] record: ', Object.keys(name_to_box))

        if (name_to_box === undefined || timestamp === undefined) throw Error()

        for (const name in name_to_box) {
            if (this.prev_pos_and_time[name] === undefined) {
                this.prev_pos_and_time[name] = []
            }
            this.prev_pos_and_time[name].push({
                box_and_embed: name_to_box[name],
                timestamp: timestamp,
            })
            if (
                this.prev_pos_and_time[name].length > this.max_record_per_person
            ) {
                this.prev_pos_and_time[name].shift()
            }
        }
    }

    get_recent() {
        const now = new Date().getTime()
        let keep = []
        for (const name in this.prev_pos_and_time) {
            for (const appear of this.prev_pos_and_time[name]) {
                let ap_time = appear.timestamp
                console.log(
                    '[appf] presene delta: ',
                    now - ap_time,
                    name,
                    now,
                    ap_time
                )
                if (now - ap_time < this.lookback_time) {
                    keep.push({
                        name: name,
                        info: appear,
                    })
                }
            }
        }
        return keep
    }

    box_metric(box_a, box_b, time_a, time_b) {
        const w_a = box_a.x_max - box_a.x_min
        const h_a = box_a.y_max - box_a.y_min
        const cw_a = (box_a.x_max + box_a.x_min) / 2
        const ch_a = (box_a.y_max + box_a.y_min) / 2

        const w_b = box_b.x_max - box_b.x_min
        const h_b = box_b.y_max - box_b.y_min
        const cw_b = (box_b.x_max + box_b.x_min) / 2
        const ch_b = (box_b.y_max + box_b.y_min) / 2

        let ratio = (w_a * h_a) / (w_b * h_b)
        ratio = ratio < 1 ? 1 / ratio : ratio

        let w_score = Math.abs(cw_a - cw_b) / Math.min(w_a, w_b)
        let h_score = Math.abs(ch_a - ch_b) / Math.min(h_a, h_b)
        let time_score = Math.abs(time_a - time_b) / 1000
        this.log(
            `[appf] w_score: `,
            w_score,
            ', h_score: ',
            h_score,
            ', time_score:',
            time_score,
            ', ratio: ',
            ratio
        )
        // this.log(
        //     `[appf] w_score: ${w_score}, ${h_score}, time_score: ${time_score}, ratio: ${ratio}`
        // )
        return ratio + w_score + h_score + time_score
    }

    async _run(data, cool_down) {
        const faces = this.use_frame
            ? data.frame
            : data.align_face_crops || data.face_crops
        const boxes = data.detect_boxes

        if (data.appearence_boxes) {
            this.record(data)
        }

        if (this.force_run(data)) {
            // not filter anything when warmup
            return data
        }

        if (faces !== undefined && boxes !== undefined) {
            boxes
                .filter((e) => e.cls == 'face')
                .map((e, i) => {
                    if (faces[i]) faces[i].box = e
                })
            const face_box_idx = boxes
                .filter((e) => e.cls == 'face')
                .map((e, i) => i)
            const prev_loc = this.get_recent()
            const now = new Date().getTime()

            for (const [index, face] of Object.entries(faces)) {
                if (face === undefined) continue
                let min_diff = 1e10
                let min_idx = -1
                let min_name = '?'

                for (let [j, face_dict] of Object.entries(prev_loc)) {
                    // for (const face_dict of prev_loc) {
                    const name = face_dict.name
                    const prev_info = face_dict.info

                    const score = this.box_metric(
                        face.box,
                        prev_info.box_and_embed,
                        now,
                        prev_info.timestamp
                    )
                    if (score < min_diff) {
                        min_diff = score
                        min_idx = j
                        min_name = name
                    }
                }

                if (min_diff < this.metric_threshold) {
                    const prev_info = prev_loc[min_idx].info // get last appear from all record we have
                    const name = prev_loc[min_idx].name
                    // debugger

                    face.embedding = prev_info.box_and_embed.embedding
                    face.uncertainty = prev_info.box_and_embed.uncertainty
                    face.id = prev_info.box_and_embed.id
                    face.name = name

                    data.detect_boxes[face_box_idx[index]].id =
                        prev_info.box_and_embed.id
                    data.detect_boxes[face_box_idx[index]].familiar =
                        prev_info.box_and_embed.familiar
                    data.detect_boxes[face_box_idx[index]].person_name = name

                    this.log(
                        '[appf] keeping something ',
                        min_diff,
                        prev_info.box_and_embed.id,
                        name
                    )
                }
            }
        }

        // delete data.detect_boxes
        return data
    }
}

export class Lambda extends Job {
    constructor(name, next, priority, pipeline, lambda_func) {
        super(name, next, priority, pipeline)
        this.func = lambda_func
    }

    cool_down() {
        return true
    }

    claim_tensors() {
        return []
    }

    async _run(data, cool_down) {
        return this.func(data)
    }
}
